import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import "./filtrosMatriculas.css";

/*
	Barra de filtros de Matrículas (Esteban, 2026-09-26): fecha (Hoy, Ayer, Esta semana, Este mes o
	un rango), asesoras, grupos de ventas y cursos, los tres de selección múltiple.
*/
export interface FiltrosMatriculasValor {
	preset: "" | "hoy" | "ayer" | "semana" | "mes" | "rango";
	dateFrom: string;
	dateTo: string;
	advisors: string[];
	groups: string[];
	courseIds: number[];
}

export interface OpcionesFiltros {
	advisors: { id: string; name: string; group: string }[];
	groups: string[];
	courses: { id: number; name: string }[];
}

export const FILTROS_VACIOS: FiltrosMatriculasValor = { preset: "", dateFrom: "", dateTo: "", advisors: [], groups: [], courseIds: [] };

export const hayFiltros = (f: FiltrosMatriculasValor) =>
	f.dateFrom !== "" || f.dateTo !== "" || f.advisors.length > 0 || f.groups.length > 0 || f.courseIds.length > 0;

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const corta = (s: string) => {
	const [y, m, d] = s.split("-").map(Number);
	return y ? `${d} ${MESES[m - 1]}` : "";
};

export function rangoDe(preset: FiltrosMatriculasValor["preset"]): { dateFrom: string; dateTo: string } {
	const hoy = new Date();
	if (preset === "hoy") {
		return { dateFrom: iso(hoy), dateTo: iso(hoy) };
	}
	if (preset === "ayer") {
		const a = new Date(hoy);
		a.setDate(hoy.getDate() - 1);
		return { dateFrom: iso(a), dateTo: iso(a) };
	}
	if (preset === "semana") {
		const lunes = new Date(hoy);
		lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7));
		return { dateFrom: iso(lunes), dateTo: iso(hoy) };
	}
	if (preset === "mes") {
		return { dateFrom: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), dateTo: iso(hoy) };
	}
	return { dateFrom: "", dateTo: "" };
}

export interface MultiProps<T extends string | number> {
	titulo: string;
	opciones: { id: T; name: string; detalle?: string }[];
	elegidos: T[];
	onChange: (v: T[]) => void;
	buscar?: boolean;
}

export function Multi<T extends string | number>({ titulo, opciones, elegidos, onChange, buscar }: MultiProps<T>) {
	const [q, setQ] = useState("");
	const visibles = useMemo(() => {
		const t = q.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
		return t === "" ? opciones : opciones.filter((o) => o.name.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(t));
	}, [q, opciones]);
	const nombreUnico = elegidos.length === 1 ? (opciones.find((o) => o.id === elegidos[0])?.name ?? "") : "";
	const alternar = (id: T) => onChange(elegidos.includes(id) ? elegidos.filter((x) => x !== id) : [...elegidos, id]);
	return (
		<Dropdown autoClose="outside" className="waFm">
			<Dropdown.Toggle as="button" type="button" className={"waFmBoton" + (elegidos.length > 0 ? " activo" : "")}>
				<span className="waFmTitulo">{titulo}</span>
				{elegidos.length === 1 ? <span className="waFmValor">{nombreUnico}</span> : ""}
				{elegidos.length > 1 ? <span className="waFmCuenta">{elegidos.length}</span> : ""}
				<FontAwesomeIcon icon={solid("chevron-down")} className="waFmFlecha" />
			</Dropdown.Toggle>
			<Dropdown.Menu className="waFmMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
				{buscar ? (
					<div className="waFmBuscar">
						<FontAwesomeIcon icon={solid("magnifying-glass")} />
						<input autoFocus type="text" placeholder="Buscar" value={q} onChange={(e) => setQ(e.target.value)} />
					</div>
				) : ""}
				<div className="waFmLista">
					{visibles.length < 1 ? <div className="waFmVacio">Sin resultados</div> : visibles.map((o) => (
						<label key={String(o.id)} className={"waFmOpcion" + (elegidos.includes(o.id) ? " elegida" : "")}>
							<input type="checkbox" checked={elegidos.includes(o.id)} onChange={() => alternar(o.id)} />
							<span className="waFmNombre">{o.name}</span>
							{o.detalle ? <span className="waFmDetalle">{o.detalle}</span> : ""}
						</label>
					))}
				</div>
				{elegidos.length > 0 ? (
					<div className="waFmPie">
						<button type="button" className="btn btn-link btn-sm" onClick={() => onChange([])}>Quitar selección</button>
					</div>
				) : ""}
			</Dropdown.Menu>
		</Dropdown>
	);
}

export interface FiltrosMatriculasProps {
	valor: FiltrosMatriculasValor;
	opciones: OpcionesFiltros | null;
	etiquetaFecha: string;
	onChange: (v: FiltrosMatriculasValor) => void;
}

const FiltrosMatriculas: React.FC<FiltrosMatriculasProps> = ({ valor, opciones, etiquetaFecha, onChange }) => {
	const [desde, setDesde] = useState(valor.dateFrom);
	const [hasta, setHasta] = useState(valor.dateTo);
	const [abierto, setAbierto] = useState(false);

	const elegirPreset = (p: FiltrosMatriculasValor["preset"]) => {
		if (valor.preset === p) {
			onChange({ ...valor, preset: "", dateFrom: "", dateTo: "" });
			return;
		}
		onChange({ ...valor, preset: p, ...rangoDe(p) });
	};
	const aplicarRango = () => {
		const [a, b] = desde && hasta && desde > hasta ? [hasta, desde] : [desde, hasta || desde];
		onChange({ ...valor, preset: a ? "rango" : "", dateFrom: a, dateTo: b });
		setAbierto(false);
	};
	const textoRango = valor.preset === "rango" && valor.dateFrom
		? (valor.dateFrom === valor.dateTo ? corta(valor.dateFrom) : `${corta(valor.dateFrom)} – ${corta(valor.dateTo)}`)
		: "";
	const presets: { key: FiltrosMatriculasValor["preset"]; label: string }[] = [
		{ key: "hoy", label: "Hoy" }, { key: "ayer", label: "Ayer" }, { key: "semana", label: "Esta semana" }, { key: "mes", label: "Este mes" }
	];

	return (
		<div className="waFmBarra">
			<div className="waFmBarraFecha">
				<span className="waFmBarraEtiqueta">{etiquetaFecha}</span>
				<div className="waFmSegs">
					{presets.map((p) => (
						<button key={p.key} type="button" className={"waFmSeg" + (valor.preset === p.key ? " activo" : "")} onClick={() => elegirPreset(p.key)}>{p.label}</button>
					))}
					<Dropdown show={abierto} onToggle={(s) => { setAbierto(s); if (s) { setDesde(valor.dateFrom); setHasta(valor.dateTo); } }} autoClose="outside">
						<Dropdown.Toggle as="button" type="button" className={"waFmSeg waFmSegRango" + (valor.preset === "rango" ? " activo" : "")}>
							<FontAwesomeIcon icon={solid("calendar-days")} />&nbsp;{textoRango || "Rango"}
						</Dropdown.Toggle>
						<Dropdown.Menu className="waFmMenu waFmRangoMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
							<div className="waFmRangoCampos">
								<label>Desde<input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} /></label>
								<label>Hasta<input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} /></label>
							</div>
							<div className="waFmRangoPie">
								<button type="button" className="btn btn-link btn-sm" onClick={() => { setDesde(""); setHasta(""); }}>Borrar</button>
								<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!desde} onClick={aplicarRango}>Aplicar</button>
							</div>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>
			<div className="waFmBarraListas">
				<Multi titulo="Asesora" buscar opciones={(opciones?.advisors ?? []).map((a) => ({ id: a.id, name: a.name, detalle: a.group }))}
					elegidos={valor.advisors} onChange={(v) => onChange({ ...valor, advisors: v })} />
				<Multi titulo="Grupo de ventas" opciones={(opciones?.groups ?? []).map((g) => ({ id: g, name: g }))}
					elegidos={valor.groups} onChange={(v) => onChange({ ...valor, groups: v })} />
				<Multi titulo="Curso" buscar opciones={(opciones?.courses ?? []).map((c) => ({ id: c.id, name: c.name.replace(/^(Diplomado|Curso|Taller)\s+(de|en)\s+/i, "") }))}
					elegidos={valor.courseIds} onChange={(v) => onChange({ ...valor, courseIds: v })} />
				{hayFiltros(valor) ? (
					<button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => onChange(FILTROS_VACIOS)}>Limpiar filtros</button>
				) : ""}
			</div>
		</div>
	);
};

export default FiltrosMatriculas;
